import { Add as AddIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSelectedAccident } from "../../../actions/accidentDashboard";
import { getAccidentBySiniestro } from "../../../actions/accidents";
import {
  cleanGlobalSearchConversations,
  closeConversation,
  globalSearchConversations,
  reopenConversation,
} from "../../../actions/messaging";
import snackbarUtils from "../../../utils/snackbarUtils";
import ConfirmDialog from "../../commons/dialog/confirmDialog";
import { StyledMessaging } from "../../styled/messaging";
import AccidentDashboardContainer from "../accident/accidentDashboard/accidentDashboardContainer";
import ConversationDetailDialog from "./conversationDetailDialog";
import MessagingSearchForm from "./messagingSearchForm";
import MessagingTable from "./messagingTable";

const MessagingContainer = ({ pending = false, waiting = false, search = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const messagingSearch = useSelector((state) => state.messaging.globalSearch);
  const art = useSelector((state) => state.art.artsAdhered);
  const sendingMessage = useSelector((state) => state.messaging.sendingMessage.isLoading);
  const selectedProvider = useSelector((state) => state.providers.selectedProvider);

  const [lastSearchFilters, setLastSearchFilters] = useState(null);

  const [displayAccidents, setDisplayAccidents] = useState("block");

  // Dialogs and states
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [conversationToView, setConversationToView] = useState(null);

  const [openConfirmCloseDialog, setOpenConfirmCloseDialog] = useState(false);
  const [conversationToClose, setConversationToClose] = useState(null);

  const [openConfirmReopenDialog, setOpenConfirmReopenDialog] = useState(false);
  const [conversationToReopen, setConversationToReopen] = useState(null);

  useEffect(() => {
    dispatch(cleanGlobalSearchConversations());

    // Auto-fetch para pending y waiting
    if (pending || waiting) {
      let stateId = null;
      if (pending) stateId = 1; // "Esperando Respuesta Prestador"
      if (waiting) stateId = 2; // "Esperando Respuesta ART"

      const defaultFilters = {
        idEstadoConversacion: pending ? 1 : 2, // ID temporal
        incluirCerrados: false,
      };

      handleSearch(defaultFilters);
    }
  }, [pending, waiting, search]);

  useEffect(() => {
    setDisplayAccidents("block");
    setOpenDetailDialog(false);
    setConversationToView(null);
    setOpenConfirmCloseDialog(false);
    setConversationToClose(null);
    setOpenConfirmReopenDialog(false);
    setConversationToReopen(null);
  }, [pending, waiting, search]);

  useEffect(() => {
    return () => {
      dispatch(cleanGlobalSearchConversations());
    }
  }, [dispatch]);

  const handleSearch = (params) => {
    const payload = { ...params, idPrestador: selectedProvider };
    dispatch(globalSearchConversations(payload));
    setLastSearchFilters(payload);
  };

  const handleResetSearch = () => {
    setLastSearchFilters(null);
    dispatch(cleanGlobalSearchConversations());
  };

  const handleRefresh = () => {
    if (lastSearchFilters) {
      handleSearch(lastSearchFilters);
    }
  };

  const handleViewAccident = (conversation) => {
    if (conversation && conversation.siniestro) {
      return dispatch(getAccidentBySiniestro(conversation.idArt, conversation.siniestro))
        .then((res) => {
          if (res && res.value && res.value.data && res.value.data.length > 0) {
            // Siniestro local encontrado, se envía con su idSiniestro int real
            dispatch(setSelectedAccident(res.value.data[0]));
            setDisplayAccidents("none");
          } else {
            // Siniestro no es local (es solo facade) o no trajo detalles extra. Se abre el Dashboard igual.
            dispatch(
              setSelectedAccident({
                siniestro: conversation.siniestro,
                idSiniestro: conversation.idSiniestroLocal || conversation.siniestro, // Si es facade puro, queda string. Si viene cruzado, queda int.
                documento: conversation.documento,
                mlCloudCoreArtId: conversation.idArt,
                art: conversation.art,
                apellidoTrabajador: conversation.trabajador,
                nombreTrabajador: "",
                mensajeriaActivada: true,
                nuevo: true
              })
            );
            setDisplayAccidents("none");
          }
        })
        .catch(() => {
          snackbarUtils.error("Error al obtener los detalles del siniestro.");
        });
    }
  };

  const handleViewMessages = (conversation) => {
    setConversationToView(conversation);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setConversationToView(null);
    // Refresh upon closing in case messages were sent
    handleRefresh();
  };

  const handleOpenConfirmCloseDialog = (conversation) => {
    setConversationToClose(conversation);
    setOpenConfirmCloseDialog(true);
  };

  const handleConfirmClose = () => {
    if (!conversationToClose) return;

    dispatch(closeConversation(conversationToClose.idConversacion, conversationToClose.idArt, conversationToClose.idTipoMensaje, conversationToClose.idSubtipoMensaje))
      .then(() => {
        setOpenConfirmCloseDialog(false);
        setConversationToClose(null);
        handleRefresh();
        snackbarUtils.success("Conversación cerrada correctamente");
      })
      .catch(() => {
        setOpenConfirmCloseDialog(false);
        setConversationToClose(null);
      });
  };

  const handleOpenConfirmReopenDialog = (conversation) => {
    setConversationToReopen(conversation);
    setOpenConfirmReopenDialog(true);
  };

  const handleConfirmReopen = () => {
    if (!conversationToReopen) return;

    dispatch(reopenConversation(conversationToReopen.idConversacion, "Conversación reabierta", conversationToReopen.idArt, conversationToReopen.idTipoMensaje, conversationToReopen.idSubtipoMensaje))
      .then(() => {
        setOpenConfirmReopenDialog(false);
        setConversationToReopen(null);
        handleRefresh();
        snackbarUtils.success("Conversación reabierta correctamente");
      })
      .catch(() => {
        setOpenConfirmReopenDialog(false);
        setConversationToReopen(null);
      });
  };

  return (
    <StyledMessaging.ContainerWrapper>
      {displayAccidents === "none" && (
        <AccidentDashboardContainer setDisplayAccidents={setDisplayAccidents} />
      )}
      <StyledMessaging.AccidentDisplayWrapper displayMode={displayAccidents}>
        {search && (
          <>
            <MessagingSearchForm
              handleSearch={handleSearch}
              handleReset={handleResetSearch}
              art={art}
              invalid={messagingSearch.isLoading}
            />
            <br />
          </>
        )}

        {(pending || waiting) && (
          <>
            <MessagingSearchForm
              dateOnly
              handleSearch={(dateFilters) => {
                const baseFilters = {
                  idEstadoConversacion: pending ? 1 : 2,
                  incluirCerrados: false,
                };
                handleSearch({ ...baseFilters, ...dateFilters });
              }}
              handleReset={() => {
                handleResetSearch();
                const baseFilters = {
                  idEstadoConversacion: pending ? 1 : 2,
                  incluirCerrados: false,
                };
                handleSearch(baseFilters);
              }}
              invalid={messagingSearch.isLoading}
            />
            <br />
          </>
        )}

        <MessagingTable
          data={messagingSearch.data}
          loading={messagingSearch.isLoading}
          searchMode={search}
          onViewMessages={handleViewMessages}
          onViewAccident={handleViewAccident}
          onCloseConversation={handleOpenConfirmCloseDialog}
          onReopenConversation={handleOpenConfirmReopenDialog}
          title={
            pending ? "Conversaciones Pendientes" :
              waiting ? "Conversaciones En Espera" :
                "Resultados de Búsqueda"
          }
          fetchData={handleRefresh}
        />

        <ConfirmDialog
          open={openConfirmCloseDialog}
          handleClose={() => setOpenConfirmCloseDialog(false)}
          handleConfirm={handleConfirmClose}
          title="¿Está seguro que desea cerrar esta conversación?"
          text="Una vez cerrada, no podrá enviar nuevos mensajes a menos que la reabra."
          loading={sendingMessage}
        />

        <ConfirmDialog
          open={openConfirmReopenDialog}
          handleClose={() => setOpenConfirmReopenDialog(false)}
          handleConfirm={handleConfirmReopen}
          title="¿Está seguro que desea reabrir esta conversación?"
          text="Esto permitirá que se sigan enviando mensajes."
          loading={sendingMessage}
        />

        {conversationToView && (
          <ConversationDetailDialog
            open={openDetailDialog}
            onClose={handleCloseDetailDialog}
            conversationId={conversationToView.idConversacion}
            idArt={conversationToView.idArt}
            idSubtipo={conversationToView.idSubtipoMensaje}
            idSiniestro={conversationToView.siniestro}
          />
        )}

        <Tooltip title="Nueva conversación" placement="left">
          <StyledMessaging.NewConversationFab
            color="primary"
            aria-label="add"
            onClick={() => navigate('/messaging/new')}
          >
            <AddIcon />
          </StyledMessaging.NewConversationFab>
        </Tooltip>
      </StyledMessaging.AccidentDisplayWrapper>
    </StyledMessaging.ContainerWrapper>
  );
};

export default MessagingContainer;
